/**
 * PDFViewer Component
 * Displays PDF document - uses iframe on desktop, PDF.js on tablets/mobile
 */

import { i18n } from '../utils/i18n.js';
import { isTouchDevice } from '../utils/deviceDetect.js';

export class PDFViewer {
  constructor({ container, pdfData, forcePdfJs = false }) {
    this.container = container;
    this.pdfData = pdfData;
    this.forcePdfJs = forcePdfJs; // Always use PDF.js (e.g. PDF modal), regardless of device
    this.pdfDoc = null;
    this.currentPage = 1;
    this.totalPages = 0;
    this.scale = 1.5;
    this.isTouch = isTouchDevice;
    this.resizeHandler = null;
    this.resizeTimeout = null;
  }

  async render() {
    // Clean up previous resize listener
    this.removeResizeListener();
    
    if (!this.pdfData) {
      this.container.innerHTML = this.renderWrapper(this.renderEmptyState());
      return;
    }

    if (this.isTouch || this.forcePdfJs) {
      await this.renderWithPdfJs();
    } else {
      this.renderWithIframe();
    }
  }

  /**
   * Add resize listener for responsive PDF.js
   */
  addResizeListener() {
    this.resizeHandler = () => {
      // Debounce resize
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.reRenderPages(), 200);
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Remove resize listener
   */
  removeResizeListener() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
  }

  /**
   * Re-render all pages (on resize)
   */
  async reRenderPages() {
    if (!this.pdfDoc) return;
    
    for (let i = 1; i <= this.totalPages; i++) {
      const canvas = this.container.querySelector(`[data-wsp-pdf-page="${i}"]`);
      if (canvas) {
        await this.renderPage(i, canvas);
      }
    }
  }

  renderWrapper(content) {
    return `
      <div class="h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        ${content}
      </div>
    `;
  }

  /**
   * Render PDF using native iframe (desktop browsers)
   */
  renderWithIframe() {
    let pdfSrc = this.getPdfSource();
    
    if (pdfSrc) {
      const separator = pdfSrc.includes('#') ? '&' : '#';
      pdfSrc = `${pdfSrc}${separator}toolbar=0&navpanes=0&view=FitH`;

      this.container.innerHTML = this.renderWrapper(`
        <iframe 
          id="wsp-pdf-iframe" 
          src="${pdfSrc}" 
          class="w-full h-full border-0"
          title="PDF Document"
        ></iframe>
      `);
    } else {
      this.container.innerHTML = this.renderWrapper(this.renderEmptyState());
    }
  }

  /**
   * Render PDF using PDF.js (tablets/mobile)
   */
  async renderWithPdfJs() {
    this.container.innerHTML = this.renderWrapper(`
      <div class="flex items-center justify-center h-full">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span class="ml-2 text-gray-600">${i18n.t('pdf.loading')}</span>
      </div>
    `);

    try {
      await this.loadPdfJs();
      await this.initPdfJs();
    } catch (error) {
      console.error('Failed to load PDF.js:', error);
      // Fallback to iframe
      this.renderWithIframe();
    }
  }

  /**
   * Load PDF.js library dynamically from CDN
   */
  async loadPdfJs() {
    if (window.pdfjsLib) return;

    const PDFJS_VERSION = '3.11.174';
    const cdnBase = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;

    // Load PDF.js script
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${cdnBase}/pdf.min.js`;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    // Configure worker
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${cdnBase}/pdf.worker.min.js`;
  }

  /**
   * Initialize PDF.js viewer
   */
  async initPdfJs() {
    const pdfData = await this.getPdfArrayBuffer();
    
    // isEvalSupported: false — CVE-2024-4367: in pdf.js < 4.2.67 a crafted PDF
    // can run JavaScript in the page while its fonts are rendered.
    this.pdfDoc = await window.pdfjsLib.getDocument({ data: pdfData, isEvalSupported: false }).promise;
    this.totalPages = this.pdfDoc.numPages;

    this.container.innerHTML = this.renderWrapper(`
      <div data-wsp-pdf-pages class="flex-1 overflow-auto bg-gray-100 flex flex-col items-center p-2 gap-2">
      </div>
    `);

    // Render all pages
    // (data attributes instead of ids: several viewers can coexist, e.g. the PDF modal)
    const pdfContainer = this.container.querySelector('[data-wsp-pdf-pages]');
    for (let i = 1; i <= this.totalPages; i++) {
      const canvas = document.createElement('canvas');
      canvas.dataset.wspPdfPage = String(i);
      canvas.className = 'shadow-md bg-white max-w-full';
      pdfContainer.appendChild(canvas);
      await this.renderPage(i, canvas);
    }

    // Add resize listener for responsive behavior
    this.addResizeListener();
  }

  /**
   * Render a specific page to a canvas
   */
  async renderPage(pageNum, canvas) {
    if (!this.pdfDoc) return;

    const page = await this.pdfDoc.getPage(pageNum);
    const ctx = canvas.getContext('2d');

    // Calculate scale to fit container width
    const containerWidth = this.container.querySelector('[data-wsp-pdf-pages]').clientWidth - 20;
    const viewport = page.getViewport({ scale: 1 });
    const scale = Math.min(containerWidth / viewport.width, this.scale);
    const scaledViewport = page.getViewport({ scale });

    // Use devicePixelRatio for sharper rendering on high-DPI displays
    const pixelRatio = window.devicePixelRatio || 1;
    
    // Set canvas size at higher resolution
    canvas.width = Math.floor(scaledViewport.width * pixelRatio);
    canvas.height = Math.floor(scaledViewport.height * pixelRatio);
    
    // Scale down with CSS for display
    canvas.style.width = `${Math.floor(scaledViewport.width)}px`;
    canvas.style.height = `${Math.floor(scaledViewport.height)}px`;

    // Scale context to match pixel ratio
    ctx.scale(pixelRatio, pixelRatio);

    await page.render({
      canvasContext: ctx,
      viewport: scaledViewport
    }).promise;
  }

  /**
   * Get PDF source URL
   */
  getPdfSource() {
    if (this.pdfData instanceof File || this.pdfData instanceof Blob) {
      return URL.createObjectURL(this.pdfData);
    }
    
    if (typeof this.pdfData === 'string') {
      if (this.pdfData.startsWith('data:')) {
        return this.pdfData;
      } else if (this.pdfData.startsWith('http') || this.pdfData.startsWith('/') || this.pdfData.endsWith('.pdf')) {
        return this.pdfData;
      } else {
        return `data:application/pdf;base64,${this.pdfData}`;
      }
    }
    
    return null;
  }

  /**
   * Get PDF as ArrayBuffer for PDF.js
   */
  async getPdfArrayBuffer() {
    if (this.pdfData instanceof File || this.pdfData instanceof Blob) {
      return await this.pdfData.arrayBuffer();
    }
    
    if (typeof this.pdfData === 'string') {
      if (this.pdfData.startsWith('data:application/pdf;base64,')) {
        const base64 = this.pdfData.split(',')[1];
        return this.base64ToArrayBuffer(base64);
      } else if (this.pdfData.startsWith('http') || this.pdfData.startsWith('/') || this.pdfData.endsWith('.pdf')) {
        const response = await fetch(this.pdfData);
        return await response.arrayBuffer();
      } else {
        // Assume base64
        return this.base64ToArrayBuffer(this.pdfData);
      }
    }
    
    throw new Error('Invalid PDF data');
  }

  /**
   * Get PDF as Blob (for printing / downloading)
   */
  async getPdfBlob() {
    if (this.pdfData instanceof Blob) {
      return this.pdfData; // File extends Blob
    }
    const buffer = await this.getPdfArrayBuffer();
    return new Blob([buffer], { type: 'application/pdf' });
  }

  /**
   * Download the PDF with the given file name
   */
  async download(fileName = 'documento.pdf') {
    const blob = await this.getPdfBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoke later: some browsers start the download asynchronously
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return blob;
  }

  /**
   * Convert base64 to ArrayBuffer
   */
  base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  renderEmptyState() {
    return `
      <div class="flex flex-col items-center justify-center h-full text-gray-400">
        <svg class="w-24 h-24 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <p class="text-lg font-medium">${i18n.t('pdf.noPdf')}</p>
      </div>
    `;
  }

  async loadPDF(pdfData) {
    this.pdfData = pdfData;
    this.currentPage = 1;
    this.pdfDoc = null;
    await this.render();
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.removeResizeListener();
    this.pdfDoc = null;
  }

  /**
   * Print the PDF document.
   *
   * Returns a Promise that resolves when the print dialog is closed
   * (afterprint). The listener is attached to the window that actually
   * performs the print (the PDF iframe on desktop, the page window for the
   * PDF.js canvas, or the hidden iframe in the fallback), because afterprint
   * fires on the window that called print(), not necessarily the page window.
   *
   * Note: afterprint fires both when the user prints and when they cancel,
   * with no way to distinguish. In some desktop browsers the native PDF
   * viewer may not emit afterprint at all; callers should not block
   * indefinitely on this Promise for critical flows.
   */
  /**
   * Print the PDF document.
   *
   * Opens the browser print dialog for the PDF. There is no reliable,
   * cross-browser way to know when the dialog is closed (the native PDF
   * viewer rendered inside an iframe does not emit 'afterprint' in some
   * desktop browsers such as Chrome), so this method does not attempt to
   * report dialog completion.
   */
  async print() {
    // Try printing from visible iframe (works when same-origin)
    const iframe = this.container.querySelector('iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.print();
        return;
      } catch (e) {
        // Cross-origin: fall through to hidden iframe approach
      }
    }

    // No visible PDF iframe (e.g. PDF.js canvas on tablet/mobile): print the
    // PDF itself via a hidden iframe with a blob URL. We must NOT use
    // window.print() here, as it would print the whole page (signer list,
    // buttons, etc.) instead of just the document.
    try {
      const blob = await this.getPdfBlob();

      const blobUrl = URL.createObjectURL(blob);
      const printFrame = document.createElement('iframe');
      printFrame.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
      printFrame.src = blobUrl;

      printFrame.onload = () => {
        try {
          printFrame.contentWindow.print();
        } finally {
          setTimeout(() => {
            document.body.removeChild(printFrame);
            URL.revokeObjectURL(blobUrl);
          }, 1000);
        }
      };

      document.body.appendChild(printFrame);
    } catch (error) {
      console.error('Failed to print PDF:', error);
      throw error;
    }
  }
}
